import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  LayoutDashboard,
  Briefcase,
  Users,
  MessageSquare,
  FolderOpen,
  UserCheck,
  Home,
  Upload,
  Calendar,
  ArrowRightLeft,
  Send,
} from "lucide-react";

interface BrokerSidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}

const translations = {
  en: {
    overview: "Overview",
    myListings: "My Listings",
    representedSellers: "Represented Sellers",
    messages: "Messages",
    documents: "Documents",
    calendar: "Calendar",
    partners: "Partners",
    experts: "Our Experts",
    dealMonitor: "Deal Monitor",
    listNewBusiness: "List New Business",
    contactRequests: "Contact Requests",
    multiRole: "Multi-Role",
    consultantDashboard: "Consultant",
  },
  fr: {
    overview: "Aperçu",
    myListings: "Mes Annonces",
    representedSellers: "Vendeurs Représentés",
    messages: "Messages",
    documents: "Documents",
    calendar: "Calendrier",
    partners: "Partenaires",
    experts: "Nos Experts",
    dealMonitor: "Moniteur de Deals",
    listNewBusiness: "Nouvelle Annonce",
    contactRequests: "Demandes de Contact",
    multiRole: "Multi-Rôle",
    consultantDashboard: "Consultant",
  },
};

export const BrokerSidebar = ({ activeTab, onTabChange }: BrokerSidebarProps) => {
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations] || translations.en;

  const navigation = [
    { id: "overview", name: t.overview, icon: Home },
    { id: "my-listings", name: t.myListings, icon: Briefcase },
    { id: "contact-requests", name: t.contactRequests, icon: Send },
    { id: "sellers", name: t.representedSellers, icon: Users },
    { id: "messages", name: t.messages, icon: MessageSquare },
    { id: "documents", name: t.documents, icon: FolderOpen },
    { id: "calendar", name: t.calendar, icon: Calendar },
    { id: "partners", name: t.partners, icon: Users },
    { id: "experts", name: t.experts, icon: UserCheck },
    { id: "list-business", name: t.listNewBusiness, icon: Upload },
  ];

  const externalLinks = [
    { href: "/listings", name: t.dealMonitor, icon: LayoutDashboard },
  ];

  return (
    <aside className="w-64 bg-card border-r border-border flex flex-col min-h-screen">
      <div className="p-6 border-b border-border">
        <Link to="/">
          <img src="/noBgColor.png" alt="AcquizGroup" className="h-10" />
        </Link>
        <p className="text-sm text-muted-foreground mt-2">Broker Portal</p>
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navigation.map((item) => {
          const isActive = activeTab === item.id;
          const Icon = item.icon;

          return (
            <button
              key={item.id}
              onClick={() => onTabChange(item.id)}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors text-left",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              )}
            >
              <Icon className="h-5 w-5" />
              {item.name}
            </button>
          );
        })}

        <div className="pt-4 mt-4 border-t border-border">
          <p className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            External
          </p>
          {externalLinks.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                to={item.href}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors text-left text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                <Icon className="h-5 w-5" />
                {item.name}
              </Link>
            );
          })}
        </div>

        <div className="pt-4 mt-4 border-t border-border">
          <p className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            {t.multiRole}
          </p>
          <Link
            to="/dashboard/consultant"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors text-left text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <ArrowRightLeft className="h-5 w-5" />
            {t.consultantDashboard}
          </Link>
        </div>
      </nav>
    </aside>
  );
};